import React, { useState } from 'react';
import { Labeled, TextField, useQuery } from 'react-admin';
import { useDataProvider, useTranslate } from 'ra-core';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import { makeStyles } from '@material-ui/core/styles';
import sortBy from 'lodash/sortBy';
import { useUserContext } from '../userContexts';
import { ApplicationApisList } from './ApplicationApisList';
import { ApplicationDetailsOverviewField } from './ApplicationDetailsOverviewField';
import { ApplicationDetailsKeyClient } from './ApplicationDetailsKeyClient';
import CollapsiblePanel from './CollapsiblePanel';
import { isPublisher, isOrgAdmin, isOrgBoundUser } from '../userContexts';
import { useLayer7Notify } from '../useLayer7Notify';
export const ApplicationDetails = ({ record }) => {
    const classes = useStyles();
    const gridClasses = useGridStyles();
    const contentLabelClasses = useContentStyles();
    const applicationDetailsOverviewClasses = useApplicationDetailsOverviewStyles();
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const notify = useLayer7Notify();
    const [userContext] = useUserContext();
    const canEdit = isPublisher(userContext) || isOrgAdmin(userContext);
    const isOrgUser = isOrgBoundUser(userContext);
    const [apiIds, setApiIds] = useState([]);
    const [apiGroupIds, setApiGroupIds] = useState([]);
    const [apiKeys, setApiKeys] = React.useState([]);
    const [customFieldsMap, setCustomFieldsMap] = React.useState({});
    const fetchApiKeys = async () => {
        const { data } = await dataProvider.getList('apiKeys', {
            applicationUuid: record.id,
            pagination: { page: 1, perPage: 100 },
            sort: { field: 'createTs', order: 'DESC' },
        }, {
            onFailure: error => notify(error),
        });
        setApiKeys(sortBy(data, ({ defaultKey }) => !defaultKey));
    };
    // get apis data
    const { data: apisData, loading: isApisDataLoading } = useQuery({
        type: 'getApis',
        resource: 'applications',
        payload: { id: record.id },
    });
    React.useEffect(() => {
        if (apisData && apisData.length > 0) {
            setApiIds(apisData.map(item => item.uuid));
        }
    }, [apisData]);
    // get api groups data
    const { data: apiGroupsIdsData, loading: isApiGroupsIdsDataLoading, } = useQuery({
        type: 'getApiGroups',
        resource: 'applications',
        payload: { id: record.id },
    });
    React.useEffect(() => {
        if (apiGroupsIdsData && apiGroupsIdsData.length > 0) {
            setApiGroupIds(apiGroupsIdsData.map(item => item.uuid));
        }
    }, [apiGroupsIdsData]);
    React.useEffect(() => {
        const fetchCustomFields = async () => {
            const { data } = await dataProvider.getList('customFields', {
                entityName: 'APPLICATION',
            }, {
                onFailure: error => notify(error),
            });
            const fieldsMap = {};
            data.map(item => (fieldsMap[item.id] = item.Name));
            setCustomFieldsMap(fieldsMap);
        };
        fetchApiKeys();
        fetchCustomFields();
    }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement(Grid, { className: classes.root, container: true, spacing: 3 },
            React.createElement(Grid, { container: true, item: true, md: 12, sm: 12, direction: "column", classes: gridClasses, className: classes.details },
                React.createElement(CollapsiblePanel, { defaultExpanded: true, label: 'resources.applications.fields.overview' },
                    React.createElement(Grid, { container: true, lg: 12, md: 12, sm: 12 },
                        React.createElement(Grid, { item: true, container: true, lg: 12, md: 12, sm: 12 },
                            React.createElement(ApplicationDetailsOverviewField, { id: "overview", classes: applicationDetailsOverviewClasses, record: record, canEdit: canEdit })),
                        React.createElement(Grid, { item: true, container: true, lg: 12, md: 12, sm: 12 },
                            React.createElement(Grid, { item: true, lg: 6, md: 6, sm: 6 },
                                React.createElement(Typography, { variant: "h3", className: classes.subsection }, translate('resources.applications.fields.details')),
                                !isOrgUser && (React.createElement(Labeled
                                // On <Labeled />, this will translate in a correct `for` attribute on the label
                                , { 
                                    // On <Labeled />, this will translate in a correct `for` attribute on the label
                                    label: "resources.applications.fields.organization", classes: contentLabelClasses, className: classes.mainField },
                                    React.createElement(TextField, { id: "organizationName", record: record, source: "organizationName" }))),
                                record.description && (React.createElement(Labeled
                                // On <Labeled />, this will translate in a correct `for` attribute on the label
                                , { 
                                    // On <Labeled />, this will translate in a correct `for` attribute on the label
                                    label: "resources.applications.fields.description", classes: contentLabelClasses, className: classes.mainField },
                                    React.createElement(Typography, { variant: "body2", className: classes.fieldContent },
                                        React.createElement("span", { id: "description" }, record.description))))),
                            React.createElement(Grid, { item: true, lg: 6, md: 6, sm: 6 },
                                React.createElement(Typography, { variant: "h3", className: classes.subsection }, translate('resources.applications.fields.customField')),
                                record.customFieldValues &&
                                    record.customFieldValues.map(item => (React.createElement(Labeled
                                    // On <Labeled />, this will translate in a correct `for` attribute on the label
                                    , { 
                                        // On <Labeled />, this will translate in a correct `for` attribute on the label
                                        label: customFieldsMap[item.customFieldUuid] || item.customFieldUuid, key: item.customFieldUuid, classes: classes, className: classes.field },
                                        React.createElement("div", { className: classes.fieldValue },
                                            React.createElement(TextField, { record: item, source: "value" })))))))))),
            React.createElement(Grid, { container: true, item: true, md: 12, sm: 12, direction: "column", classes: gridClasses, className: classes.details }, (apiIds.length > 0 || apiGroupIds.length > 0) &&
                !isApisDataLoading &&
                !isApiGroupsIdsDataLoading && (React.createElement(Grid, { className: classes.root, container: true },
                React.createElement(Grid, { item: true, container: true, direction: "row", justify: "center", md: 12, sm: 12 },
                    React.createElement(ApplicationApisList, { apiIds: apiIds, apiGroupIds: apiGroupIds, application: record }))))),
            React.createElement(Grid, { container: true, item: true, md: 12, sm: 12, direction: "column", classes: gridClasses, className: classes.configuration, justify: "flex-start" },
                React.createElement(Grid, { item: true },
                    React.createElement(Typography, { variant: "h3", className: classes.subtitle }, translate('resources.applications.fields.authCredentials'))),
                React.createElement(Divider, null),
                React.createElement(List, { className: classes.mainField }, apiKeys.map(apiKey => (React.createElement(ApplicationDetailsKeyClient, { appUuid: record.id, id: apiKey.id, key: apiKey.id, data: apiKey, includeSecret: true, labelClasses: contentLabelClasses, dataProvider: dataProvider, refreshApiKeys: fetchApiKeys }))))))));
};
const useStyles = makeStyles(theme => ({
    root: {
        display: 'flex',
        fontFamily: theme.typography.body2.fontFamily,
        fontSize: theme.typography.caption.fontSize,
        margin: theme.spacing(0),
        width: '100%',
    },
    details: {
        padding: `${theme.spacing(1)}px ${theme.spacing(3)}px !important`,
    },
    configuration: {
        padding: `${theme.spacing(1)}px ${theme.spacing(3)}px !important`,
    },
    subsection: {
        color: '#333333',
        fontWeight: 600,
        fontSize: '18px',
    },
    subtitle: {
        color: theme.palette.primary.main || '#333333',
        fontWeight: theme.typography.fontWeightBold,
        fontSize: '21px',
        lineHeight: '22px',
        margin: theme.spacing(1, 0, 1, 0),
    },
    field: {
        marginLeft: theme.spacing(2),
        marginRight: theme.spacing(1),
        minWidth: '100px',
        width: '100%',
    },
    label: {
        color: theme.palette.primary.textHub || '#333333',
        fontFamily: theme.typography.textHub,
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 'bold',
        lineHeight: '22px',
        transform: 'unset',
    },
    mainField: {
        minWidth: '100px',
        padding: 0,
        width: '100%',
    },
    fieldValue: {
        color: theme.palette.primary.textHub || '#333333',
        fontFamily: theme.typography.textHub,
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 'normal',
        lineHeight: '20px',
    },
    type: {
        textTransform: 'uppercase',
    },
    icon: {
        fontSize: '1rem',
    },
    chip: {
        marginLeft: theme.spacing(1),
        height: theme.spacing(3),
    },
}), {
    name: 'Layer7ApplicationDetails',
});
const useApplicationDetailsOverviewStyles = makeStyles(theme => ({
    markdown: {
        overflowY: 'scroll',
        height: '200px',
        paddingRight: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
}));
const useContentStyles = makeStyles(theme => ({
    label: {
        color: theme.palette.primary.textHub || '#333333',
        fontFamily: theme.typography.textHub,
        fontWeight: 700,
        fontSize: '14px',
        lineHeight: '22px',
        margin: theme.spacing(1, 0, 1, 0),
        transform: 'unset',
    },
}));
const useGridStyles = makeStyles(theme => ({
    root: {
        borderBottom: 'none',
    },
}));
//# sourceMappingURL=ApplicationDetails.js.map